/**
 * Antes/depois da estética (F2.4) — a convenção do `item_ref` e o pareamento.
 * Fonte única do app do mecânico, do app do cliente, do painel, da página
 * compartilhada e do backoffice.
 *
 * O par NÃO é uma tabela: são duas linhas comuns de `os_media`, com o
 * `item_ref` dizendo o papel e a peça — `before:capo` e `after:capo`. Assim o
 * par nasce offline, sobe pela fila de upload que já existe e chega ao
 * servidor sem migração nem rota nova. Quem junta as duas em par é
 * `pairBeforeAfter`, aqui.
 *
 * Refazer é tirar de novo: vale a foto MAIS RECENTE de cada papel. A antiga
 * fica guardada (a mídia da OS não se apaga sozinha), só sai do par.
 *
 * Para quem não conhece o prefixo, a foto é EXECUÇÃO (`classifyMediaCategory`
 * não tem ramo para ele, como não tem para `stage:`): é o registro do
 * trabalho feito, e é em "o que foi feito" que o app antigo do cliente a mostra
 * — com o rótulo "Antes · Capô" que a API já manda.
 */
import {
  BEFORE_AFTER_PANELS,
  BEFORE_AFTER_ROLE_LABELS,
  isBeforeAfterPanel,
  type BeforeAfterPanel,
  type BeforeAfterRole,
} from '@mustauto/types/before-after';
import { getVehiclePanelLabel } from '@mustauto/types/vehicle-panels';
import { PACKAGE_MODULE } from '@mustauto/types/esthetic-packages';

/** O módulo da OS tem antes/depois? Hoje, só a estética. */
export function moduleHasBeforeAfter(module: string | null | undefined): boolean {
  return module === PACKAGE_MODULE;
}

export const BEFORE_PREFIX = 'before:';
export const AFTER_PREFIX = 'after:';

const PREFIX: Record<BeforeAfterRole, string> = { before: BEFORE_PREFIX, after: AFTER_PREFIX };

/** O `item_ref` da foto — quem grava usa isto, nunca o texto na mão. */
export function beforeAfterRef(role: BeforeAfterRole, panel: BeforeAfterPanel): string {
  return `${PREFIX[role]}${panel}`;
}

export function isBeforeAfterRef(itemRef: string | null | undefined): boolean {
  const ref = itemRef ?? '';
  return ref.startsWith(BEFORE_PREFIX) || ref.startsWith(AFTER_PREFIX);
}

/** Papel e peça de um `item_ref`, ou `null` (não é antes/depois, ou a peça é desconhecida). */
export function parseBeforeAfterRef(
  itemRef: string | null | undefined,
): { role: BeforeAfterRole; panel: BeforeAfterPanel } | null {
  const ref = itemRef ?? '';
  const role: BeforeAfterRole | null = ref.startsWith(BEFORE_PREFIX)
    ? 'before'
    : ref.startsWith(AFTER_PREFIX)
      ? 'after'
      : null;
  if (!role) return null;
  const panel = ref.slice(PREFIX[role].length);
  return isBeforeAfterPanel(panel) ? { role, panel } : null;
}

/** Nome da peça ("Farol esquerdo"). */
export function beforeAfterPanelLabel(panel: BeforeAfterPanel): string {
  return getVehiclePanelLabel(panel) ?? 'Peça';
}

/**
 * "Antes · Capô" — o rótulo da foto sozinha (galeria do app antigo do cliente,
 * auditoria do backoffice, nome do arquivo baixado). Peça desconhecida vira
 * "Antes" / "Depois", nunca a chave crua.
 */
export function beforeAfterLabel(itemRef: string | null | undefined): string | null {
  const ref = itemRef ?? '';
  const parsed = parseBeforeAfterRef(ref);
  if (parsed) return `${BEFORE_AFTER_ROLE_LABELS[parsed.role]} · ${beforeAfterPanelLabel(parsed.panel)}`;
  if (ref.startsWith(BEFORE_PREFIX)) return BEFORE_AFTER_ROLE_LABELS.before;
  if (ref.startsWith(AFTER_PREFIX)) return BEFORE_AFTER_ROLE_LABELS.after;
  return null;
}

export interface BeforeAfterPair<T> {
  panel: BeforeAfterPanel;
  label: string;
  before: T | null;
  after: T | null;
}

/**
 * As mídias da OS em pares por peça, na ordem de volta do carro. Só foto; o
 * vídeo nunca entra num par. De cada papel vale a mais recente (`createdAt`);
 * empate fica com a que vier por último. Peça sem nenhuma das duas não
 * aparece; com uma só, aparece com o outro lado `null` — é o "falta o depois"
 * que a tela da OS cobra.
 */
export function pairBeforeAfter<
  T extends { itemRef?: string | null; createdAt?: string | number | Date | null; type?: string | null; mediaType?: string | null },
>(media: readonly T[]): BeforeAfterPair<T>[] {
  const latest = new Map<string, { item: T; at: number }>();
  for (const item of media) {
    const kind = item.type ?? item.mediaType ?? 'photo';
    if (kind !== 'photo') continue;
    const parsed = parseBeforeAfterRef(item.itemRef);
    if (!parsed) continue;
    const key = `${parsed.role}:${parsed.panel}`;
    const ms = item.createdAt ? new Date(item.createdAt).getTime() : 0;
    const at = Number.isNaN(ms) ? 0 : ms;
    const current = latest.get(key);
    if (!current || current.at <= at) {
      latest.set(key, { item, at });
    }
  }
  const pairs: BeforeAfterPair<T>[] = [];
  for (const panel of BEFORE_AFTER_PANELS) {
    const before = latest.get(`before:${panel}`)?.item ?? null;
    const after = latest.get(`after:${panel}`)?.item ?? null;
    if (before || after) pairs.push({ panel, label: beforeAfterPanelLabel(panel), before, after });
  }
  return pairs;
}

/**
 * As mídias da OS separadas em três: o par de cada peça, as SOLTAS (tudo que
 * não é foto de antes/depois — vão para "o que foi feito") e as SUBSTITUÍDAS
 * (antes/depois refeito: a foto antiga do mesmo lado). Fonte única de quem
 * mostra o par — galeria do cliente, painel, página compartilhada — e da API,
 * que tira as substituídas da lista do app antigo: o "Refazer" existe para a
 * foto ruim sumir, e não para ela continuar na galeria com o nome "Antes".
 *
 * Foto com prefixo mas peça desconhecida (gravada por uma versão futura) não
 * forma par e fica entre as soltas, com o rótulo "Antes"/"Depois".
 */
export function splitBeforeAfter<
  T extends {
    id: string;
    itemRef?: string | null;
    createdAt?: string | number | Date | null;
    type?: string | null;
    mediaType?: string | null;
  },
>(media: readonly T[]): { pairs: BeforeAfterPair<T>[]; solo: T[]; supersededIds: Set<string> } {
  const pairs = pairBeforeAfter(media);
  const inPair = new Set<string>();
  for (const p of pairs) {
    if (p.before) inPair.add(p.before.id);
    if (p.after) inPair.add(p.after.id);
  }
  const supersededIds = new Set<string>();
  const solo: T[] = [];
  for (const m of media) {
    const kind = m.type ?? m.mediaType ?? 'photo';
    const paired = kind === 'photo' && parseBeforeAfterRef(m.itemRef) !== null;
    if (!paired) solo.push(m);
    else if (!inPair.has(m.id)) supersededIds.add(m.id);
  }
  return { pairs, solo, supersededIds };
}
